import { NavLink } from "react-router";
import { motion } from "framer-motion";
import { useSEO } from "../../hooks/useSEO";

import Carousel from "../../components/Carousel";

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import FootballLottie from "../../lottiefiles/football2.json";
export default function Home(): JSX.Element {
  useSEO({
    title: "Best Sports Medicine Clinic in Chennai | Orthopedic & Rehab Care",
    description: "Expert sports medicine, physiotherapy, and orthopedic rehabilitation in Anna Nagar, Chennai. We help athletes recover faster. Book an appointment today!",
    keywords: "sports medicine chennai, best physiotherapist anna nagar, orthopedic doctor, sports injury recovery"
  });

  return (
    <div className="bg-gray-50">
      {/* Top Carousel Section */}
      <section className="w-full overflow-hidden">
        <Carousel />
      </section>

      <section className="relative bg-indigo-600 text-white overflow-hidden py-12 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Column 1: Animation */}
            <div className="w-full relative z-10 flex justify-center items-center">
              <div className="w-full max-w-md md:max-w-full">
                <DotLottieReact
                  data={FootballLottie}
                  loop
                  autoplay
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Column 2: Content */}
            <div className="relative z-10 text-center md:text-left">
              <motion.h1
                className="text-4xl font-extrabold sm:text-6xl"
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Comprehensive Sports Medicine & Rehabilitation
              </motion.h1>
              <motion.p
                className="mt-6 text-lg sm:text-xl text-indigo-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                Helping athletes and active individuals recover, perform, and
                prevent injuries with world-class medical expertise.
              </motion.p>
              <motion.div
                className="mt-8 flex justify-center md:justify-start gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.7 }}
              >
                <NavLink
                  to="/services"
                  className="rounded-xl bg-white px-6 py-3 text-indigo-600 font-semibold shadow-md hover:bg-indigo-50"
                >
                  Explore Services
                </NavLink>
                <NavLink
                  to="/about"
                  className="rounded-xl bg-indigo-500 px-6 py-3 font-semibold hover:bg-indigo-700"
                >
                  Learn More
                </NavLink>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <motion.h2
          className="text-3xl font-bold text-indigo-600 text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Our Specialized Services
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              name: "Shoulder Care",
              desc: "Advanced diagnosis and treatment for shoulder injuries.",
            },
            {
              name: "Knee Treatment",
              desc: "Comprehensive care for ligament, cartilage, and joint problems.",
            },
            {
              name: "Back & Neck",
              desc: "Solutions for chronic pain, spine alignment, and rehabilitation.",
            },
            {
              name: "Sports Therapy",
              desc: "Personalized therapy to restore mobility and prevent future injuries.",
            },
            {
              name: "Sports Psychology",
              desc: "Mental strength training for peak performance.",
            },
            {
              name: "PRP & Stem Cell Therapy",
              desc: "Regenerative treatments for long-term healing.",
            },
          ].map((service, idx) => (
            <motion.div
              key={idx}
              className="rounded-2xl bg-white p-6 shadow-md hover:shadow-lg"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {service.name}
              </h3>
              <p className="text-gray-600">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About Snippet */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <motion.img
            src="/images/sports-medicine.png"
            alt="About our clinic"
            className="rounded-2xl shadow-md w-full h-[400px] object-cover"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          />
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-indigo-600 mb-4">
              About Our Clinic
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our sports medicine clinic is dedicated to helping athletes and
              active individuals recover from injuries and improve their
              performance. With a team of experts in orthopedics, physiotherapy,
              and sports science, we provide world-class care tailored to your
              unique needs.
            </p>
            <NavLink
              to="/about"
              className="inline-block rounded-xl bg-indigo-600 px-6 py-3 text-white font-semibold hover:bg-indigo-700"
            >
              Read More
            </NavLink>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-100 py-16">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <motion.h2
            className="text-3xl font-bold text-indigo-600 mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            What Our Patients Say
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                quote:
                  "The doctors helped me recover from a major knee injury and get back on the field faster than I imagined.",
                name: "Rahul Sharma",
              },
              {
                quote:
                  "I had constant back pain, but with their treatment plan, Im finally pain-free and active again!",
                name: "Anjali Mehta",
              },
              {
                quote:
                  "The sports psychology sessions improved my focus and confidence before competitions.",
                name: "Vikram Singh",
              },
            ].map((t, idx) => (
              <motion.div
                key={idx}
                className="rounded-2xl bg-white p-6 shadow-md hover:shadow-lg"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
              >
                <p className="text-gray-700 italic mb-4">{t.quote}</p>
                <h4 className="font-semibold text-gray-900">{t.name}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <motion.section
        className="relative bg-indigo-600 text-white py-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/sports-psychology.png')" }}
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-indigo-900/60" />

        {/* Content */}
        <div className="relative mx-auto max-w-7xl px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Book Your Appointment Today
          </h2>
          <p className="mb-6 text-indigo-100">
            Dont let injuries hold you back. Get expert care from our sports
            medicine specialists.
          </p>
          <NavLink
            to="/contact"
            className="rounded-xl bg-white px-6 py-3 text-indigo-600 font-semibold shadow-md hover:bg-indigo-50"
          >
            Contact Us
          </NavLink>
        </div>
      </motion.section>
    </div>
  );
}
